import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, Trash2, Undo2 } from "lucide-react";
import { api, errMsg, fmtSGT } from "@/lib/api";

export function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const ban = async (u, banned) => {
    const reason = banned ? window.prompt(`Ban ${u.display_name}? They will be logged out, hidden from the leaderboard and unable to log in.\nReason (optional):`, "") : "";
    if (banned && reason === null) return;
    try { await api.put(`/admin/users/${u.id}/ban`, { banned, reason: reason || null }); toast.success(banned ? "Account banned" : "Account reinstated"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Permanently delete ${u.display_name} (${u.email}) and all their scores? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Account removed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!users) return <p className="eyebrow" data-testid="admin-user-loading">Loading users…</p>;
  const visible = users.filter((u) => !q || u.display_name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="eyebrow" data-testid="admin-user-count">{users.length} accounts · {users.filter((u) => u.status === "banned").length} banned</p>
        <input className="field w-44 py-2 text-sm" placeholder="Search name / email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-user-search" />
      </div>
      <ul className="panel divide-y divide-slate-800" data-testid="admin-user-list">
        {visible.length === 0 && <li className="p-4 text-sm text-slate-500">No accounts match.</li>}
        {visible.map((u) => (
          <li key={u.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`admin-user-${u.id}`}>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{u.display_name}
                {u.role === "admin" && <span className="ml-1 font-mono text-[10px] uppercase text-amber-400">admin</span>}
                {u.status === "banned" && <span className="ml-1 rounded bg-rose-500/20 px-1.5 py-0.5 font-mono text-[10px] uppercase text-rose-300" data-testid={`admin-user-banned-${u.id}`}>banned</span>}
              </p>
              <p className="truncate text-xs text-slate-500">{u.email} · joined {fmtSGT(u.created_at)}{u.ban_reason ? ` · ${u.ban_reason}` : ""}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <div className="text-right">
                <p className="font-mono text-sm font-semibold">{u.total_points}</p>
                <p className="text-[10px] text-slate-500">{u.challenges_completed} done</p>
              </div>
              {u.role !== "admin" && (
                <>
                  <button onClick={() => ban(u, u.status !== "banned")} className={`rounded-md p-2 hover:bg-slate-800 ${u.status === "banned" ? "text-emerald-400" : "text-slate-400 hover:text-amber-300"}`} aria-label={u.status === "banned" ? "Unban" : "Ban"} data-testid={`admin-user-ban-${u.id}`}>{u.status === "banned" ? <Undo2 size={15} /> : <Ban size={15} />}</button>
                  <button onClick={() => remove(u)} className="rounded-md p-2 text-slate-400 hover:bg-slate-800 hover:text-rose-400" aria-label="Delete" data-testid={`admin-user-delete-${u.id}`}><Trash2 size={15} /></button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
